import { isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';
import { v4 as uuid } from 'uuid';

import { monitorTimeout } from '@/api/monitorTimeout.ts';
import type { DealRejected, DealReply, FillReport } from '@/api/signalR/messages/execution.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { cashApi } from '@/store/cash/api/index.ts';
import { closeCashRfs } from '@/store/cash/api/requestCashRfs.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { buildHandleLateFillReport } from '@/store/growl/lateFillReport.ts';
import { getExecution, GrowlSlice, type GrowUpdate } from '@/store/growl/slice.ts';
import { updateGrowlApiErrorThunk } from '@/store/growl/updateGrowlApiErrorThunk.ts';
import { whenGrowlClosed } from '@/store/growl/whenGrowlClosed.ts';
import type { AppDispatch, AppState, GetAppState } from '@/store/store.ts';
import type { BidAskPair } from '@/types/common.ts';
import type { ISODate } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';
import { SECOND_IN_MILLIS } from '@/utils/time.ts';
import { toBase64 } from '@/utils/toBase64.ts';

import {
  cashExecutionPending,
  cashExecutionRejected,
  cashExecutionReset,
  cashExecutionSuccess,
  cashRejectedExecutionReset,
} from '../slice.ts';

export type ExecuteCashRfsParams = ExecuteCashSpotRfsParams | ExecuteCashForwardRfsParams | ExecuteCashNdfRfsParams;

export type ExecuteCashSpotRfsParams = {
  productName: 'FxSpot';
  tileId: string;

  currency1: string;
  currency2: string;
  notional: number;
  notionalCurrencyIndex: CurrencyIndex;
  maturityDate: ISODate;
  clientId: number; // 20252

  signalRConnectionId: string; // 'YD5N_o_U38c2YHOIgJcpSgMkzBMQd02';
  rfsId: string; // 'SGM_crorxa4f4yydh26070z4fzzjn';
  quoteId: string; // 'to_be_accepted.1';
  way: 'Bid' | 'Ask';
  priceWithoutMargin: number; // 2.2532;
  priceWithMargin: number; // 2.25313;
  spotWithoutMargin: BidAskPair;
  spotWithMargin: BidAskPair;
  forwardPoints: number; // -4;
  userLocalDateTime: Timestamp;
  lastQuoteFromBackTimestamp: Timestamp; // 1745846111268;
  lastQuoteReceivedByFrontTimestamp: Timestamp; // 1745846111163;
  executionClickByFrontTimestamp: Timestamp; // 1745846112360;
  notificationEmail: string;
};

export type ExecuteCashForwardRfsParams = {
  productName: 'FxFwd';
  tileId: string;

  currency1: string;
  currency2: string;
  notional: number;
  notionalCurrencyIndex: CurrencyIndex;
  maturityDate: ISODate;
  spotRate: number;
  clientId: number; // 20252

  signalRConnectionId: string; // 'YD5N_o_U38c2YHOIgJcpSgMkzBMQd02';
  rfsId: string; // 'SGM_crorxa4f4yydh26070z4fzzjn';
  quoteId: string; // 'to_be_accepted.1';
  way: 'Bid' | 'Ask';
  priceWithoutMargin: number; // 2.2532;
  priceWithMargin: number; // 2.25313;
  spotWithoutMargin: BidAskPair;
  spotWithMargin: BidAskPair;
  forwardPoints: number; // -4;
  userLocalDateTime: Timestamp;
  lastQuoteFromBackTimestamp: Timestamp; // 1745846111268;
  lastQuoteReceivedByFrontTimestamp: Timestamp; // 1745846111163;
  executionClickByFrontTimestamp: Timestamp; // 1745846112360;
  notificationEmail: string;
};

export type ExecuteCashNdfRfsParams = {
  productName: 'FxNdf';
  tileId: string;

  currency1: string;
  currency2: string;
  notional: number;
  notionalCurrencyIndex: CurrencyIndex;
  maturityDate: ISODate;
  spotRate: number;
  clientId: number; // 20252

  signalRConnectionId: string; // 'YD5N_o_U38c2YHOIgJcpSgMkzBMQd02';
  rfsId: string; // 'SGM_crorxa4f4yydh26070z4fzzjn';
  quoteId: string; // 'to_be_accepted.1';
  way: 'Bid' | 'Ask';
  priceWithoutMargin: number; // 2.2532;
  priceWithMargin: number; // 2.25313;
  spotWithoutMargin: BidAskPair;
  spotWithMargin: BidAskPair;
  forwardPoints: number; // -4;
  userLocalDateTime: Timestamp;
  lastQuoteFromBackTimestamp: Timestamp; // 1745846111268;
  lastQuoteReceivedByFrontTimestamp: Timestamp; // 1745846111163;
  executionClickByFrontTimestamp: Timestamp; // 1745846112360;
  notificationEmail: string;

  reference: string; // WMR1PMLN
  fixingDate: ISODate;
};

export type ExecuteCashRfsResponse = {};

// fx-web/app/src/api/multipass/cash/rfs/cashRfsQuoteExecution.ts

export const executeCashRfs = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ExecuteCashRfsResponse, ExecuteCashRfsParams>({
    query: (body) => {
      return {
        url: '/fxSpotFwd/rfs/execute',
        method: 'POST',
        // headers: { 'X-Correlation-ID': body.executionId },
        body: {
          replyToStream: body.signalRConnectionId,
          rfsId: body.rfsId,
          quoteId: body.quoteId,
          way: body.way,
          // priceWithoutMargin = CashQuoteReply.SpotWithoutMargin (raw backend value, fallback to 0 if null)
          //
          // priceWithMargin depends on the user type, computed in getRfsPrices() (store/cash/selectors/rfs.ts):
          //   - Internal Sales: recomputed as SpotWithoutMargin ± (10 × commercialMarginSpotPoints)
          //                     where commercialMarginSpotPoints comes from CashQuoteReply.DefaultSpotMargin
          //                     (fallback to 0 if null), and can be edited by the user before execution
          //   - External / Internal Trader: taken directly from CashQuoteReply.SpotWithMargin (raw backend value)
          priceWithoutMargin: body.way === 'Bid' ? body.spotWithoutMargin.bid : body.spotWithoutMargin.ask,
          priceWithMargin: body.way === 'Bid' ? body.spotWithMargin.bid : body.spotWithMargin.ask,
          forwardPoints: body.forwardPoints,
          userLocalDateTime: format(body.userLocalDateTime, "yyyy-MM-dd'T'hh:mm:ss"),
          lastQuoteFromBackTimestamp: body.lastQuoteFromBackTimestamp,
          lastQuoteReceivedByFrontTimestamp: body.lastQuoteReceivedByFrontTimestamp,
          executionClickByFrontTimestamp: body.executionClickByFrontTimestamp,
          email64NotificationList: toBase64(body.notificationEmail),
        },
      };
    },

    async onQueryStarted(request, { queryFulfilled, getState, dispatch }) {
      const { tileId, rfsId } = request;

      const stopMonitor = monitorTimeout(rfsId, dispatch, {
        onExecFail: () => {
          dispatchCashExecutionRejected(tileId, rfsId, dispatch);
        },
        onExecSuccess: () => {
          dispatchCashExecutionSuccess(tileId, rfsId, getState as GetAppState, dispatch);
        },
        getPendingExecutionDetails: () => {
          const execution = getExecution(getState() as AppState, rfsId);
          if (
            isNotDefined(execution) ||
            execution.type !== 'cashDealExecution' ||
            execution.data.status !== 'pending'
          ) {
            return undefined;
          }

          return {
            clientId: execution.data.data.accountId,
            currencyPair: `${execution.data.data.currency1}/${execution.data.data.currency2}`,
            productType: execution.data.productName,
            tradeDate: execution.data.data.tradeDate,
          };
        },
      });

      try {
        await queryFulfilled;
      } catch (error) {
        stopMonitor();
        dispatch(updateGrowlApiErrorThunk({ growlId: rfsId, error }));
        dispatchCashExecutionRejected(tileId, rfsId, dispatch);
      }
    },

    async onCacheEntryAdded(params, { cacheEntryRemoved, getState, dispatch }) {
      const handleReceiveDealReply = buildHandleReceiveDealReply(params, getState as GetAppState, dispatch);
      const handleReceiveDealRejected = buildHandleReceiveDealRejected(params, getState as GetAppState, dispatch);
      const handleReceiveFillReport = buildHandleReceiveFillReport(params, getState as GetAppState, dispatch);

      const signalRConnector = getSignalRConnector();

      signalRConnector.on('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.on('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.on('ReceiveFillReport', handleReceiveFillReport);

      // signalRConnector.offHeartbeatTimeout(params.rfsId);
      dispatch(
        GrowlSlice.actions.create({
          growlId: params.rfsId,
          growl: {
            type: 'cashDealExecution',
            createdAt: Date.now(),
            options: { closeable: false },
            data:
              params.productName === 'FxSpot'
                ? {
                    tileId: params.tileId,
                    productName: 'FxSpot',
                    status: 'pending',
                    data: {
                      way: params.way,
                      currency1: params.currency1,
                      currency2: params.currency2,

                      accountId: params.clientId,

                      notional: params.notional,
                      notionalCurrencyIndex: params.notionalCurrencyIndex,

                      dealtRate: params.priceWithMargin,
                      spotDate: params.maturityDate,
                      tradeDate: params.userLocalDateTime,
                    },
                  }
                : params.productName === 'FxFwd'
                  ? {
                      tileId: params.tileId,
                      productName: 'FxFwd',
                      status: 'pending',
                      data: {
                        way: params.way,
                        currency1: params.currency1,
                        currency2: params.currency2,

                        accountId: params.clientId,

                        notional: params.notional,
                        notionalCurrencyIndex: params.notionalCurrencyIndex,

                        dealtRate: params.priceWithMargin,
                        spotRate: params.spotRate,
                        swapPoints: params.forwardPoints,
                        forwardDate: params.maturityDate,
                        tradeDate: params.userLocalDateTime,
                      },
                    }
                  : {
                      tileId: params.tileId,
                      productName: 'FxNdf',
                      status: 'pending',
                      data: {
                        way: params.way,
                        currency1: params.currency1,
                        currency2: params.currency2,

                        accountId: params.clientId,

                        notional: params.notional,
                        notionalCurrencyIndex: params.notionalCurrencyIndex,

                        dealtRate: params.priceWithMargin,
                        forwardDate: params.maturityDate,
                        reference: params.reference,
                        fixingDate: params.fixingDate,
                        tradeDate: params.userLocalDateTime,
                      },
                    },
          },
        }),
      );

      dispatch(cashExecutionPending({ tileId: params.tileId, executionId: params.rfsId }));

      // `cacheEntryRemoved` is always resolved explicitly via `removeMutationResult`,
      // called on every execution path: DealReply, DealRejected, FillReport, and timeout.
      // It is never left to RTK Query's automatic 1-hour cache expiry.
      await cacheEntryRemoved;

      // stop the RFS stream (signalR listeners, timeouts, etc)
      closeCashRfs(params.tileId, dispatch);

      // To close the listeners
      signalRConnector.off('ReceiveDealReply', handleReceiveDealReply);
      signalRConnector.off('ReceiveDealRejected', handleReceiveDealRejected);
      signalRConnector.off('ReceiveFillReport', handleReceiveFillReport);

      // the back office can send the trade id long after the execution request is over
      // (NOT_YET_INTEGRATED): keep listening, but only on behalf of the growl, and only as long as
      // the user keeps it open
      const handleLateFillReport = buildHandleLateFillReport(params.rfsId, dispatch);
      signalRConnector.on('ReceiveFillReport', handleLateFillReport);

      await whenGrowlClosed(params.rfsId, getState as GetAppState);

      signalRConnector.off('ReceiveFillReport', handleLateFillReport);
    },
  });
};

// ███████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗         ██████╗
// ██╔════╝██║██╔════╝ ████╗  ██║██╔══██╗██║         ██╔══██╗
// ███████╗██║██║  ███╗██╔██╗ ██║███████║██║         ██████╔╝
// ╚════██║██║██║   ██║██║╚██╗██║██╔══██║██║         ██╔══██╗
// ███████║██║╚██████╔╝██║ ╚████║██║  ██║███████╗    ██║  ██║
// ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝    ╚═╝  ╚═╝

// SEE: fx-web/app/src/Workers/SignalR/signalRStream.ts
const TIMEOUT_TOTAL_MILLIS = 20 * SECOND_IN_MILLIS;

const buildHandleReceiveDealReply = (params: ExecuteCashRfsParams, getState: GetAppState, dispatch: AppDispatch) => {
  return (dealReply: DealReply) => {
    if (dealReply.Execute.RfsId !== params.rfsId) {
      return;
    }

    dispatchCashExecutionSuccess(params.tileId, params.rfsId, getState, dispatch);

    // FillReport is expected after DealReply and will provide trade IDs + close listeners.
    // If no FillReport arrives within TIMEOUT_TOTAL_MILLIS, close listeners as fallback.
    setTimeout(() => {
      const execution = getExecution(getState(), params.rfsId);

      if (isNotDefined(execution) || execution.type !== 'cashDealExecution' || execution.data.status !== 'integrated') {
        return;
      }

      dispatch(cashApi.internalActions.removeMutationResult({ fixedCacheKey: params.rfsId }));
    }, TIMEOUT_TOTAL_MILLIS);
  };
};

const dispatchCashExecutionSuccess = (tileId: string, rfsId: string, getState: GetAppState, dispatch: AppDispatch) => {
  dispatch(
    GrowlSlice.actions.update({
      growlId: rfsId,
      data: { status: 'integrated' },
      options: {
        closeable: true,
      },
    }),
  );

  dispatch(cashExecutionSuccess({ tileId }));

  setTimeout(() => {
    // FxFwd tiles lose maturityDateTenor after reset — capture it before dispatching
    const tileBeforeReset = selectCashTile(getState(), tileId);
    const maturityDateTenor = tileBeforeReset?.properties.maturityDateTenor;

    dispatch(cashExecutionReset({ tileId, userPreferences: getState().userPreferences, sessionId: uuid() }));

    // TODO: can be not a Cash tile if the user doesn't have access to Cash
    const resetedTile = selectCashTile(getState(), tileId);

    if (isNotDefined(resetedTile) || isNotDefined(resetedTile.tradeCapture)) {
      return;
    }

    dispatch(
      // @ts-expect-error special case when init FxCash
      cashApi.endpoints.checkCashByTradeCapture.initiate({
        tileId,
        type: 'FxSpot',
        properties: {
          currencyPair: resetedTile.properties.currencyPair,
          notional: resetedTile.properties.notional,
          notionalCurrencyIndex: resetedTile.properties.notionalCurrencyIndex,
          maturityDate: maturityDateTenor !== undefined ? undefined : resetedTile.properties.maturityDate,
          maturityDateTenor: maturityDateTenor ?? resetedTile.properties.maturityDateTenor,
        },
      }),
    );
  }, SECOND_IN_MILLIS);
};

const dispatchCashExecutionRejected = (tileId: string, rfsId: string, dispatch: AppDispatch) => {
  dispatch(cashApi.internalActions.removeMutationResult({ fixedCacheKey: rfsId }));
  dispatch(cashExecutionRejected({ tileId }));

  setTimeout(() => dispatch(cashRejectedExecutionReset({ tileId })), 2 * SECOND_IN_MILLIS);
};

const buildHandleReceiveDealRejected = (
  params: ExecuteCashRfsParams,
  _getState: GetAppState,
  dispatch: AppDispatch,
) => {
  return (rejectedDeal: DealRejected) => {
    if (rejectedDeal.Execute.RfsId !== params.rfsId) {
      return;
    }

    const data: GrowUpdate = {
      status: 'rejected',
      errorCode: rejectedDeal.DealReject.ErrorCode,
      multipassErrorCode: rejectedDeal.DealReject.MultipassErrorCode,
      reason: rejectedDeal.DealReject.ErrorMessage,
      sessionId: rejectedDeal.DealReject.MultipassSessionId,
    };
    dispatch(GrowlSlice.actions.update({ growlId: params.rfsId, data, options: { closeable: true } }));
    dispatchCashExecutionRejected(params.tileId, rejectedDeal.Execute.RfsId, dispatch);
  };
};

const buildHandleReceiveFillReport = (params: ExecuteCashRfsParams, _getState: GetAppState, dispatch: AppDispatch) => {
  const updateGrowl = buildHandleLateFillReport(params.rfsId, dispatch);

  return (fillReport: FillReport) => {
    if (fillReport.Execute.RfsId !== params.rfsId) {
      return;
    }

    updateGrowl(fillReport);

    dispatch(cashApi.internalActions.removeMutationResult({ fixedCacheKey: params.rfsId }));

    dispatch(cashExecutionSuccess({ tileId: params.tileId }));
  };
};
